import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Copy, Image as ImageIcon, X, Eye, EyeOff, ArrowLeft, Clock, CalendarDays } from 'lucide-react';
import { nacti, posli, stylKarty, stylPole, tlumene, cara, Nadpis } from '../sprava/ui';
import { muze } from '../../lib/moduly';
import NahratMedia from '../NahratMedia';
import MediaPickerModal from '../MediaPickerModal';

/**
 * Kalendář akcí webu (Obchod → Web → Akce, 2026-10-28).
 *
 * Seznam nadcházejících (a proběhlých jako vzor k zopakování) a editor
 * s živým náhledem karty, jak ji ukáže úvod fofrlama.cz. Úvod bere
 * nejbližší tři zveřejněné, akce zmizí 3 h po začátku.
 */
const TYPY = {
    jidlo: { nazev: 'Jídlo', barva: '#FF8A00' },
    deti: { nazev: 'Pro děti', barva: '#F59ACB' },
    kviz: { nazev: 'Kvíz', barva: '#C9A2F0' },
    prenos: { nazev: 'Přenos', barva: '#7FD4F0' },
    soutez: { nazev: 'Soutěž', barva: '#FFB45C' },
    gaming: { nazev: 'Gaming', barva: '#7FD4F0' },
    komunita: { nazev: 'Komunita', barva: '#C9A2F0' },
};
const PODKLAD = { jidlo: '#B56302', soutez: '#B56302', deti: '#B0407E', prenos: '#1C6E8C', gaming: '#1C6E8C', kviz: '#6C4192', komunita: '#6C4192' };
const ODKAZY = [
    { text: 'Rezervovat stůl', url: '/bistro' },
    { text: 'Do menu', url: '/menu' },
    { text: 'Získat FofrKartu', url: '/fofrkarta' },
];
const DNY = ['ne', 'po', 'út', 'st', 'čt', 'pá', 'so'];
const DNY_CELE = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];
const MESICE = ['led', 'úno', 'bře', 'dub', 'kvě', 'čvn', 'čvc', 'srp', 'zář', 'říj', 'lis', 'pro'];

const prazdna = () => ({ id: null, nazev: '', typ: 'komunita', datum: '', cas: '18:00', popis: '', stitky: [], odkaz_text: '', odkaz_url: '', media_soubor_id: null, foto: null, zverejneno: true });
const zAkce = (a) => ({ ...prazdna(), ...a, datum: a.kdy.slice(0, 10), cas: a.kdy.slice(11, 16), odkaz_text: a.odkaz?.text || '', odkaz_url: a.odkaz?.url || '' });
const datumZ = (datum, cas) => (datum ? new Date(`${datum}T${cas || '00:00'}`) : null);
const popisKdy = (d) => (d ? `${DNY_CELE[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : 'datum a čas');

export default function WebAkce({ user, isDark, isMobile, barva }) {
    const [data, setData] = useState({ nadchazejici: [], probehle: [] });
    const [chyba, setChyba] = useState('');
    const [form, setForm] = useState(null);
    const [ukladam, setUkladam] = useState(false);
    const [knihovna, setKnihovna] = useState(false);
    const [stitek, setStitek] = useState('');
    const smi = muze(user, 'web.akce', 'upravuje');
    const karta = useMemo(() => stylKarty(isDark), [isDark]);
    const pole = useMemo(() => stylPole(isDark), [isDark]);

    const nactiVse = () => nacti('/api/web-akce').then(setData).catch(e => setChyba(e.message));
    useEffect(() => { nactiVse(); }, []);

    const zmen = (patch) => setForm(f => ({ ...f, ...patch }));
    const uloz = async () => {
        if (!form.nazev.trim() || !form.datum) { setChyba('Vyplň název a datum.'); return; }
        setUkladam(true); setChyba('');
        try {
            const telo = {
                nazev: form.nazev.trim(), typ: form.typ, kdy: `${form.datum} ${form.cas || '00:00'}`, popis: form.popis.trim() || null,
                stitky: form.stitky, odkaz_text: form.odkaz_text.trim() || null, odkaz_url: form.odkaz_url.trim() || null,
                media_soubor_id: form.media_soubor_id, zverejneno: form.zverejneno,
            };
            const ulozena = await posli(form.id ? `/api/web-akce/${form.id}` : '/api/web-akce', form.id ? 'PUT' : 'POST', telo);
            setForm(zAkce(ulozena));
            nactiVse();
        } catch (e) { setChyba(e.message); }
        setUkladam(false);
    };
    const smaz = async () => {
        if (!confirm(`Smazat akci „${form.nazev}"?`)) return;
        try { await posli(`/api/web-akce/${form.id}`, 'DELETE'); setForm(null); nactiVse(); } catch (e) { setChyba(e.message); }
    };
    const pridejStitek = () => {
        const t = stitek.trim();
        if (t && form.stitky.length < 4 && !form.stitky.includes(t)) zmen({ stitky: [...form.stitky, t] });
        setStitek('');
    };
    const fotka = (soubor) => zmen({ media_soubor_id: soubor.id, foto: soubor.url || (soubor.cesta ? `/storage/${soubor.cesta}` : null) });

    const radek = (a, probehla = false) => {
        const d = datumZ(a.kdy.slice(0, 10), a.kdy.slice(11, 16));
        const on = form?.id === a.id;
        return (
            <button key={a.id} type="button" onClick={() => setForm(zAkce(a))}
                className="w-full flex items-center gap-3 text-left"
                style={{ padding: '10px 12px', background: on ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)') : 'transparent', border: 'none',
                    borderLeft: `3px solid ${on ? barva : 'transparent'}`, borderBottom: `1px solid ${cara(isDark)}`, cursor: 'pointer', opacity: probehla ? 0.6 : 1 }}>
                <span className="flex flex-col items-center justify-center shrink-0" style={{ width: 46, height: 50, borderRadius: 10, border: `1px solid ${TYPY[a.typ]?.barva}66`, lineHeight: 1.05 }}>
                    <small style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: TYPY[a.typ]?.barva }}>{DNY[d.getDay()]}</small>
                    <b style={{ fontSize: 19, color: 'var(--text-primary)' }}>{d.getDate()}</b>
                    <small style={{ fontSize: 10, fontWeight: 700, color: tlumene(isDark) }}>{MESICE[d.getMonth()]}</small>
                </span>
                <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{a.nazev}</span>
                    <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {TYPY[a.typ]?.nazev} · {a.kdy.slice(11, 16)}
                        {!a.zverejneno && <span className="inline-flex items-center gap-1" style={{ color: '#F59E0B' }}><EyeOff size={11} /> skrytá</span>}
                    </span>
                </span>
            </button>
        );
    };

    const seznam = (
        <div className="rounded-xl overflow-hidden" style={karta}>
            <div className="flex items-center justify-between gap-2" style={{ padding: '12px 12px 8px' }}>
                <Nadpis isDark={isDark}>Nadcházející</Nadpis>
                {smi && (
                    <button type="button" onClick={() => setForm(prazdna())} className="inline-flex items-center gap-1.5 rounded-lg"
                        style={{ height: 34, padding: '0 12px', fontSize: 13, fontWeight: 600, background: barva, color: '#fff', border: 'none', cursor: 'pointer' }}>
                        <Plus size={14} /> Nová akce
                    </button>
                )}
            </div>
            {data.nadchazejici.length ? data.nadchazejici.map(a => radek(a)) : (
                <p className="text-sm" style={{ padding: '6px 14px 16px', color: tlumene(isDark) }}>Zatím žádná akce. Web teď místo karet ukazuje „Program se připravuje“.</p>
            )}
            {data.probehle.length > 0 && (
                <>
                    <div style={{ padding: '14px 12px 0' }}><Nadpis isDark={isDark}>Proběhlé</Nadpis></div>
                    {data.probehle.map(a => radek(a, true))}
                </>
            )}
        </div>
    );

    const d = form && datumZ(form.datum, form.cas);
    const typ = form && (TYPY[form.typ] || TYPY.komunita);
    const nahled = form && (
        <div style={{ borderRadius: 20, overflow: 'hidden', background: 'linear-gradient(180deg, #241E26, #121112)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', maxWidth: 360 }}>
            <div style={{ position: 'relative', height: 130,
                background: form.foto ? `linear-gradient(180deg, rgba(0,0,0,.15), rgba(0,0,0,.55)), url(${form.foto}) center/cover`
                    : `radial-gradient(70% 90% at 85% 10%, ${PODKLAD[form.typ]}d9, transparent 70%), linear-gradient(135deg, #1d1420, #141214)` }}>
                <span style={{ position: 'absolute', left: 14, top: 14, display: 'grid', justifyItems: 'center', minWidth: 58, padding: '6px 8px', borderRadius: 14, background: 'rgba(12,10,12,.72)', border: `1px solid ${typ.barva}73`, lineHeight: 1 }}>
                    <small style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', color: typ.barva }}>{d ? DNY[d.getDay()] : '—'}</small>
                    <b style={{ fontSize: 30, fontFamily: 'Anton, Impact, sans-serif', fontWeight: 400 }}>{d ? d.getDate() : '?'}</b>
                    <small style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', color: typ.barva }}>{d ? MESICE[d.getMonth()] : ''}</small>
                </span>
                <span style={{ position: 'absolute', right: 12, top: 14, padding: '5px 10px', borderRadius: 99, background: 'rgba(12,10,12,.6)', color: typ.barva, fontSize: 12, fontWeight: 800 }}>{typ.nazev}</span>
            </div>
            <div style={{ display: 'grid', gap: 7, padding: '14px 16px 16px' }}>
                <b style={{ fontSize: 18, lineHeight: 1.2 }}>{form.nazev || 'Název akce'}</b>
                <span className="inline-flex items-center gap-1.5" style={{ fontSize: 13, fontWeight: 700, color: typ.barva }}><Clock size={13} /> {popisKdy(d)}</span>
                {form.popis && <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'rgba(255,255,255,.72)' }}>{form.popis}</p>}
                {form.stitky.length > 0 && (
                    <span className="flex flex-wrap gap-1.5">{form.stitky.map(s => <span key={s} style={{ padding: '3px 9px', borderRadius: 99, border: '1px solid rgba(255,255,255,.16)', fontSize: 11.5, fontWeight: 700, color: 'rgba(255,255,255,.75)' }}>{s}</span>)}</span>
                )}
                {form.odkaz_text && <span style={{ justifySelf: 'start', marginTop: 4, padding: '8px 14px', borderRadius: 12, background: '#FF8A00', color: '#1C0D00', fontSize: 13, fontWeight: 800 }}>{form.odkaz_text} ›</span>}
            </div>
        </div>
    );

    const popisek = (t) => <p style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 5px' }}>{t}</p>;
    const editor = form && (
        <div className="rounded-xl" style={{ ...karta, padding: 16 }}>
            <div className="flex items-center gap-2 mb-3">
                {isMobile && <button type="button" onClick={() => setForm(null)} aria-label="Zpět" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}><ArrowLeft size={18} /></button>}
                <b className="flex-1 text-base" style={{ color: 'var(--text-primary)' }}>{form.id ? 'Upravit akci' : 'Nová akce'}</b>
                {form.id && smi && (
                    <button type="button" title="Zopakovat jako novou" onClick={() => setForm({ ...form, id: null, datum: '' })}
                        className="inline-flex items-center gap-1 text-xs" style={{ height: 30, padding: '0 10px', borderRadius: 8, background: 'none', border: `1px solid ${cara(isDark)}`, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                        <Copy size={13} /> Zopakovat
                    </button>
                )}
                {form.id && smi && (
                    <button type="button" title="Smazat" onClick={smaz} style={{ width: 30, height: 30, borderRadius: 8, background: 'none', border: `1px solid ${cara(isDark)}`, color: '#EF4444', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><Trash2 size={14} /></button>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1fr) 340px', gap: 20, alignItems: 'start' }}>
                <fieldset disabled={!smi} style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: 14 }}>
                    <div>{popisek('Název')}<input value={form.nazev} maxLength={120} onChange={e => zmen({ nazev: e.target.value })} placeholder="Pub kvíz s FofrLamou" style={pole} /></div>
                    <div>
                        {popisek('Typ — barva a ikona karty')}
                        <div className="flex flex-wrap gap-1.5">
                            {Object.entries(TYPY).map(([k, t]) => (
                                <button key={k} type="button" onClick={() => zmen({ typ: k })} className="rounded-lg text-xs font-semibold"
                                    style={{ height: 32, padding: '0 12px', cursor: 'pointer', background: form.typ === k ? `${t.barva}26` : 'transparent', color: form.typ === k ? t.barva : 'var(--text-secondary)', border: `1px solid ${form.typ === k ? t.barva : cara(isDark)}` }}>
                                    {t.nazev}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <div style={{ flex: 1 }}>{popisek('Datum')}<input type="date" value={form.datum} onChange={e => zmen({ datum: e.target.value })} style={pole} /></div>
                        <div style={{ width: 120 }}>{popisek('Začátek')}<input type="time" value={form.cas} onChange={e => zmen({ cas: e.target.value })} style={pole} /></div>
                    </div>
                    <div>
                        {popisek(`Popis (${form.popis.length}/400)`)}
                        <textarea value={form.popis} maxLength={400} rows={3} onChange={e => zmen({ popis: e.target.value })} placeholder="Jedna dvě věty: co tě čeká a proč přijít."
                            style={{ ...pole, height: 'auto', padding: '8px 10px', resize: 'vertical' }} />
                    </div>
                    <div>
                        {popisek('Štítky (max 4) — Zdarma, Pro děti, −20 %…')}
                        <div className="flex flex-wrap items-center gap-1.5">
                            {form.stitky.map(s => (
                                <span key={s} className="inline-flex items-center gap-1 rounded-full text-xs" style={{ padding: '4px 6px 4px 10px', border: `1px solid ${cara(isDark)}`, color: 'var(--text-primary)' }}>
                                    {s}<button type="button" aria-label={`Odebrat ${s}`} onClick={() => zmen({ stitky: form.stitky.filter(x => x !== s) })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tlumene(isDark), display: 'grid' }}><X size={12} /></button>
                                </span>
                            ))}
                            {form.stitky.length < 4 && (
                                <input value={stitek} maxLength={24} onChange={e => setStitek(e.target.value)} onBlur={pridejStitek}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); pridejStitek(); } }} placeholder="+ štítek" style={{ ...pole, width: 130, height: 30 }} />
                            )}
                        </div>
                    </div>
                    <div>
                        {popisek('Tlačítko na kartě (nepovinné)')}
                        <div className="flex gap-2">
                            <input value={form.odkaz_text} maxLength={40} onChange={e => zmen({ odkaz_text: e.target.value })} placeholder="Text tlačítka" style={{ ...pole, flex: 1 }} />
                            <input value={form.odkaz_url} maxLength={255} onChange={e => zmen({ odkaz_url: e.target.value })} placeholder="/bistro nebo https://…" style={{ ...pole, flex: 1 }} />
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                            {ODKAZY.map(o => (
                                <button key={o.url} type="button" onClick={() => zmen({ odkaz_text: o.text, odkaz_url: o.url })} className="text-xs rounded-md"
                                    style={{ height: 26, padding: '0 9px', background: 'none', border: `1px dashed ${cara(isDark)}`, color: 'var(--text-secondary)', cursor: 'pointer' }}>{o.text}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        {popisek('Fotka (nepovinné) — jinak barevný plakát podle typu')}
                        <div className="flex flex-wrap items-center gap-2">
                            {form.foto && <img src={form.foto} alt="" style={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 8 }} />}
                            <div style={{ minWidth: 200, flex: 1 }}><NahratMedia isDark={isDark} accept="image/*" vyska={64} accent={barva} popisek="Přetáhni fotku nebo vyber z počítače" onNahrano={fotka} /></div>
                            <button type="button" onClick={() => setKnihovna(true)} className="inline-flex items-center gap-1.5 text-xs rounded-lg" style={{ height: 32, padding: '0 10px', background: 'none', border: `1px solid ${cara(isDark)}`, color: 'var(--text-primary)', cursor: 'pointer' }}><ImageIcon size={13} /> Z knihovny</button>
                            {form.foto && <button type="button" onClick={() => zmen({ media_soubor_id: null, foto: null })} className="text-xs" style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer' }}>Odebrat</button>}
                        </div>
                    </div>
                    <button type="button" onClick={() => zmen({ zverejneno: !form.zverejneno })} className="flex items-center gap-3 rounded-lg text-left"
                        style={{ padding: '10px 12px', border: `1px solid ${form.zverejneno ? barva : cara(isDark)}`, background: 'transparent', cursor: 'pointer' }}>
                        {form.zverejneno ? <Eye size={16} style={{ color: barva }} /> : <EyeOff size={16} style={{ color: tlumene(isDark) }} />}
                        <span className="flex-1 text-sm" style={{ color: 'var(--text-primary)' }}>{form.zverejneno ? 'Zveřejněno na webu' : 'Skrytá — jen rozpracovaná'}</span>
                    </button>
                    {chyba && <p className="text-sm" style={{ color: '#EF4444', margin: 0 }}>{chyba}</p>}
                    {smi && (
                        <div className="flex gap-2 justify-end">
                            <button type="button" onClick={() => { setForm(null); setChyba(''); }} style={{ height: 40, padding: '0 16px', borderRadius: 8, background: 'transparent', color: 'var(--text-secondary)', border: `1px solid ${cara(isDark)}`, cursor: 'pointer' }}>Zavřít</button>
                            <button type="button" onClick={uloz} disabled={ukladam} style={{ height: 40, padding: '0 18px', borderRadius: 8, background: barva, color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', opacity: ukladam ? 0.6 : 1 }}>{ukladam ? 'Ukládám…' : 'Uložit'}</button>
                        </div>
                    )}
                </fieldset>
                <div style={{ position: isMobile ? 'static' : 'sticky', top: 0 }}>
                    {popisek('Náhled na webu')}
                    {nahled}
                </div>
            </div>
            <MediaPickerModal open={knihovna} onClose={() => setKnihovna(false)} accept="image" onSelect={(_, soubor) => fotka(soubor)} />
        </div>
    );

    if (isMobile) return <div className="px-1 pb-24">{form ? editor : seznam}</div>;

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '340px minmax(0, 1fr)', gap: 16, alignItems: 'start', paddingBottom: 24 }}>
            {seznam}
            {editor || (
                <div className="rounded-xl flex flex-col items-center justify-center gap-2 text-center" style={{ ...karta, minHeight: 260, padding: 24 }}>
                    <CalendarDays size={28} style={{ color: barva }} />
                    <b style={{ color: 'var(--text-primary)' }}>Kalendář akcí webu</b>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)', maxWidth: 420 }}>
                        Úvod fofrlama.cz ukazuje nejbližší tři zveřejněné akce, stránka Akce všechny nadcházející. Akce zmizí 3 hodiny po začátku.
                    </p>
                    {chyba && <p className="text-sm" style={{ color: '#EF4444' }}>{chyba}</p>}
                </div>
            )}
        </div>
    );
}
